import { Coins, Mic, Timer } from "lucide-react"
import * as React from "react"
import { Link } from "react-router-dom"
import { toast } from "sonner"

import { PageHeader } from "@/components/layout"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Skeleton } from "@/components/ui/skeleton"
import { ApiError, api } from "@/lib/api"
import type { AdminStt, AdminSttRow } from "@/lib/types"
import { formatCostUsd, formatDuration } from "@/lib/utils"

const n = (value: number) => value.toLocaleString()

const DAY_OPTIONS = ["7", "30", "90"] as const

function StatCard({
  icon: Icon,
  label,
  value,
  hint,
}: {
  icon: React.ComponentType<{ className?: string }>
  label: string
  value: string | number
  hint?: string
}) {
  return (
    <Card>
      <CardContent className="flex items-start gap-3 p-4">
        <div className="bg-muted text-muted-foreground rounded-md p-2">
          <Icon className="size-4" />
        </div>
        <div className="min-w-0">
          <div className="text-muted-foreground text-xs font-medium">{label}</div>
          <div className="text-2xl font-semibold leading-tight">{value}</div>
          {hint && <div className="text-muted-foreground mt-0.5 text-xs">{hint}</div>}
        </div>
      </CardContent>
    </Card>
  )
}

function SttTable({ title, rows }: { title: string; rows: AdminSttRow[] }) {
  return (
    <div>
      <h2 className="mb-3 text-sm font-semibold">{title}</h2>
      {rows.length === 0 ? (
        <p className="text-muted-foreground text-sm">No transcriptions in this window.</p>
      ) : (
        <div className="overflow-x-auto rounded-lg border">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-muted-foreground">
              <tr>
                <th className="px-4 py-2 text-left font-medium">Name</th>
                <th className="px-4 py-2 text-right font-medium">Calls</th>
                <th className="px-4 py-2 text-right font-medium">Audio</th>
                <th className="px-4 py-2 text-right font-medium">Tokens</th>
                <th className="px-4 py-2 text-right font-medium">Est. cost</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.label} className="border-t">
                  <td className="px-4 py-2">{row.label}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{n(row.calls)}</td>
                  <td className="px-4 py-2 text-right tabular-nums">
                    {formatDuration(row.audio_seconds * 1000)}
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums">{n(row.total_tokens)}</td>
                  <td className="px-4 py-2 text-right tabular-nums">
                    {formatCostUsd(row.cost_usd)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

export function AdminSttPage() {
  const [days, setDays] = React.useState("30")
  const [stt, setStt] = React.useState<AdminStt | null>(null)
  const [loading, setLoading] = React.useState(true)

  React.useEffect(() => {
    setLoading(true)
    api
      .adminStt(Number(days))
      .then(setStt)
      .catch((error) =>
        toast.error(error instanceof ApiError ? error.message : "Could not load STT usage."),
      )
      .finally(() => setLoading(false))
  }, [days])

  return (
    <>
      <PageHeader
        title="STT usage"
        description="Speech-to-text transcription usage across every account, metered on its own."
        action={
          <div className="flex items-center gap-2">
            <Select value={days} onValueChange={setDays}>
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DAY_OPTIONS.map((d) => (
                  <SelectItem key={d} value={d}>
                    Last {d} days
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button asChild variant="outline">
              <Link to="/admin">Back to admin</Link>
            </Button>
          </div>
        }
      />
      <div className="space-y-6 p-6">
        {loading || !stt ? (
          <Skeleton className="h-32 w-full" />
        ) : (
          <>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
              <StatCard
                icon={Coins}
                label="Estimated cost"
                value={formatCostUsd(stt.totals.cost_usd)}
                hint={`over the last ${stt.days} days`}
              />
              <StatCard icon={Mic} label="Transcriptions" value={n(stt.totals.calls)} />
              <StatCard
                icon={Timer}
                label="Audio"
                value={formatDuration(stt.totals.audio_seconds * 1000)}
              />
              <StatCard icon={Coins} label="Tokens" value={n(stt.totals.total_tokens)} />
            </div>
            <p className="text-muted-foreground text-xs">
              {stt.provider
                ? `Cost is estimated at the current STT provider (${stt.provider}); historical usage on a different provider may be mispriced.`
                : "No STT provider is configured, so cost is estimated at $0."}
            </p>
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              <SttTable title="By user" rows={stt.by_user} />
              <SttTable title="By workspace" rows={stt.by_workspace} />
            </div>
          </>
        )}
      </div>
    </>
  )
}
